import { useState } from 'react';
import { useGeolocation, classifyCheckInFix } from '../hooks/useGeolocation';
import type { GeolocationPosition } from '../hooks/useGeolocation';
import GpsAccuracyBanner from './GpsAccuracyBanner';

// What blocked an auto check-in — drives the override modal copy so a coarse
// fix reads as "GPS unreliable, retry" instead of a confident "you are 45km
// away" (#2817 / diag #62).
type CheckInIssueCode = 'off_site' | 'low_accuracy' | 'no_gps' | 'no_coords';
interface CheckInIssue {
  code: CheckInIssueCode;
  distance: number | null;
  accuracy: number | null;
}

function formatDistance(meters: number): string {
  return meters < 1000 ? `${Math.round(meters)}m` : `${(meters / 1000).toFixed(1)}km`;
}

/**
 * Pick the best fix from a batch collected over the watch window (#2817 / diag
 * #62). Prefer a fix that lands within the geofence with usable accuracy — the
 * CLOSEST such fix wins, so a true on-site fix beats a stray far-but-accurate
 * Wi-Fi outlier gathered in the same window. If none are on-site, fall back to
 * the most precise fix so the caller can still classify low_accuracy vs off_site.
 * Pure + exported for unit testing.
 */
export function pickBestFix(
  fixes: GeolocationPosition[],
  storeLat: number,
  storeLng: number,
  radiusMeters: number,
  distOf: (lat1: number, lng1: number, lat2: number, lng2: number) => number
): GeolocationPosition | null {
  if (fixes.length === 0) return null;
  const scored = fixes.map((f) => ({ f, d: distOf(f.latitude, f.longitude, storeLat, storeLng) }));
  const onSite = scored.filter((x) => x.d <= radiusMeters && (x.f.accuracy == null || x.f.accuracy <= radiusMeters));
  if (onSite.length > 0) return onSite.reduce((a, b) => (b.d < a.d ? b : a)).f;
  return scored.reduce((a, b) => ((b.f.accuracy ?? Infinity) < (a.f.accuracy ?? Infinity) ? b : a)).f;
}

interface GPSCheckInProps {
  storeLat: number;
  storeLng: number;
  storeName: string;
  maxDistanceMeters?: number;
  onCheckIn: (gpsOverrideReason?: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

export default function GPSCheckIn({
  storeLat,
  storeLng,
  storeName,
  maxDistanceMeters = 200, // Default 200m radius
  onCheckIn,
  isLoading = false,
  disabled = false,
}: GPSCheckInProps) {
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [issue, setIssue] = useState<CheckInIssue | null>(null);

  const {
    position,
    error: geoError,
    isLoading: isGettingLocation,
    getCurrentPosition,
    sampleFixes,
    calculateDistance,
  } = useGeolocation();

  const handleCheckIn = async () => {
    // If store has no coordinates, we can't verify location - always prompt for override
    const storeHasCoordinates = storeLat !== 0 && storeLng !== 0 && storeLat != null && storeLng != null;

    // Get current position (we want this even if store has no coords, to record user's location)
    let pos = await getCurrentPosition();

    if (!pos) {
      setIssue({ code: 'no_gps', distance: null, accuracy: null });
      setShowOverrideModal(true);
      return;
    }

    if (!storeHasCoordinates) {
      // Store has no coordinates - can't verify, always show override prompt
      setIssue({ code: 'no_coords', distance: null, accuracy: pos.accuracy });
      setShowOverrideModal(true);
      return;
    }

    let distance = calculateDistance(pos.latitude, pos.longitude, storeLat, storeLng);

    // When the first fix is unusable (far or coarse), WATCH for a short window
    // instead of taking a single re-sample (#2817 / diag #62). A persistent
    // Wi-Fi/network fix can be tens of km off with confident accuracy, and a
    // lone re-sample can return the same wrong-but-confident position; watching
    // a few seconds lets the receiver refine to the true on-site GPS fix. We
    // early-stop the moment a within-radius, usable-accuracy fix arrives, then
    // pick the best of everything gathered. Happy path (a good first fix) never
    // enters this branch.
    const firstFixBad =
      distance > maxDistanceMeters || (pos.accuracy != null && pos.accuracy > maxDistanceMeters);
    if (firstFixBad) {
      const isOnSite = (f: GeolocationPosition) =>
        calculateDistance(f.latitude, f.longitude, storeLat, storeLng) <= maxDistanceMeters &&
        (f.accuracy == null || f.accuracy <= maxDistanceMeters);
      const samples = await sampleFixes({ durationMs: 10000, shouldStop: isOnSite });
      const best = pickBestFix([pos, ...samples], storeLat, storeLng, maxDistanceMeters, calculateDistance);
      if (best) {
        pos = best;
        distance = calculateDistance(pos.latitude, pos.longitude, storeLat, storeLng);
      }
    }

    const code = classifyCheckInFix(distance, pos.accuracy, maxDistanceMeters);
    if (code === 'verified') {
      onCheckIn();
    } else {
      // 'off_site' (trusted-far) vs 'low_accuracy' (coarse+far, ambiguous).
      setIssue({ code, distance, accuracy: pos.accuracy });
      setShowOverrideModal(true);
    }
  };

  const handleOverrideCheckIn = () => {
    if (!overrideReason.trim()) return;
    // Pass the override reason to be saved with the call log
    const reason = overrideReason.trim();
    setShowOverrideModal(false);
    setOverrideReason('');
    setIssue(null);
    onCheckIn(reason);
  };

  // Re-run the check from the modal — a fresh fix often clears a transient
  // coarse/outlier reading without needing an override reason at all.
  const handleRetry = () => {
    setShowOverrideModal(false);
    setOverrideReason('');
    setIssue(null);
    void handleCheckIn();
  };

  const distance = position
    ? calculateDistance(position.latitude, position.longitude, storeLat, storeLng)
    : null;

  const isWithinRange = distance !== null && distance <= maxDistanceMeters;

  return (
    <>
      <div className="space-y-3">
        {/* Accuracy warning (above all status so reps see it first) */}
        {position && <GpsAccuracyBanner accuracyMeters={position.accuracy} />}

        {/* Location Status */}
        {position && (
          <div
            className={`flex items-center gap-2 rounded-lg p-3 ${
              isWithinRange ? 'bg-green-50' : 'bg-amber-50'
            }`}
          >
            <svg
              className={`h-5 w-5 ${isWithinRange ? 'text-green-600' : 'text-amber-600'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <div className="flex-1">
              <p className={`text-sm font-medium ${isWithinRange ? 'text-green-700' : 'text-amber-700'}`}>
                {isWithinRange ? 'At store location' : 'Not at store location'}
              </p>
              <p className={`text-xs ${isWithinRange ? 'text-green-600' : 'text-amber-600'}`}>
                {distance !== null
                  ? distance < 1000
                    ? `${Math.round(distance)}m away`
                    : `${(distance / 1000).toFixed(1)}km away`
                  : 'Distance unknown'}
                {position.accuracy > 50 && ` (accuracy: ${Math.round(position.accuracy)}m)`}
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {geoError && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3">
            <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <p className="text-sm font-medium text-red-700">Location Error</p>
              <p className="text-xs text-red-600">{geoError.message}</p>
            </div>
          </div>
        )}

        {/* Check In Button */}
        <button
          onClick={handleCheckIn}
          disabled={isLoading || isGettingLocation || disabled}
          className="flex w-full items-center justify-center gap-3 rounded-xl bg-green-600 py-4 font-semibold text-white shadow-lg transition-all hover:bg-green-700 active:scale-[0.98] disabled:opacity-70"
        >
          {isLoading || isGettingLocation ? (
            <>
              <svg className="h-6 w-6 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span>{isGettingLocation ? 'Getting Location...' : 'Checking In...'}</span>
            </>
          ) : (
            <>
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span>Check In with GPS</span>
            </>
          )}
        </button>
      </div>

      {/* Override Modal */}
      {showOverrideModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                <svg className="h-6 w-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">
                  {issue?.code === 'low_accuracy' ? 'GPS Signal Unreliable' : 'Location Override Required'}
                </h3>
                <p className="text-sm text-slate-500">
                  {issue?.code === 'low_accuracy'
                    ? `Your GPS fix is imprecise${
                        issue.accuracy != null ? ` (±${Math.round(issue.accuracy)}m)` : ''
                      } — we can't confirm you're at ${storeName}. Move to an open area and retry, or check in with a reason.`
                    : issue?.code === 'no_gps' || geoError
                      ? 'Unable to verify your location'
                      : issue?.code === 'no_coords'
                        ? `This store has no saved location — check in to record where you are.`
                        : `You appear to be ${
                            issue?.distance != null ? formatDistance(issue.distance) : 'far'
                          } from ${storeName}`}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Please provide a reason
              </label>
              <textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="e.g., GPS signal weak inside store, parked in back lot..."
                className="w-full rounded-lg border border-slate-200 p-3 text-sm placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                rows={3}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowOverrideModal(false);
                  setOverrideReason('');
                  setIssue(null);
                }}
                className="flex-1 rounded-lg border border-slate-200 py-2.5 font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                Cancel
              </button>
              {issue?.code !== 'no_coords' && (
                <button
                  onClick={handleRetry}
                  disabled={isGettingLocation}
                  className="flex-1 rounded-lg border border-primary-500 py-2.5 font-medium text-primary-600 transition-colors hover:bg-primary-50 disabled:opacity-60"
                >
                  Retry GPS
                </button>
              )}
              <button
                onClick={handleOverrideCheckIn}
                disabled={!overrideReason.trim()}
                className="flex-1 rounded-lg bg-amber-600 py-2.5 font-medium text-white transition-colors hover:bg-amber-700 disabled:bg-slate-300"
              >
                Override & Check In
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
